import { useState, useEffect } from 'react';
import {
  useSaveGreetingCardAndEmailDraftMutation,
  useCreateRegistryPurchaseContextMutation,
  useSendRSVPBasicNoteMutation,
  GetDataForThanksForRSVPQuery,
  ShippingAddressInput,
} from '@graphql/generated';
import { Colors } from '@withjoy/joykit/theme';
import { useDisclosure } from '@withjoy/joykit';
import { useFeatureValue } from '@shared/core/featureFlags';
import { useTranslation } from '@shared/core';
import { useQueryParamHelper } from '@shared/core/queryString';
import { withWindow } from '@shared/utils/withWindow';
import { IGiftWrapTheme, isRegistryGiftWrapEcardPricePayload, prepareECardPayload, useGenerateGiftNote } from '@shared/components';
import { EGiftWrapType } from '@shared/components/GiftWrap/GiftWrapTypes'; // Bypass `@shared/components` to avoid circular dependency
import { useThanksForRSVPTelemetry } from '@apps/thanksForRsvp/ThanksForRSVP.telemetry';
import { GiftWrapType } from '@apps/registry/guest/GuestRegistry.telemetry';
import { greetingCardBaseUrl } from '@apps/greetingCard/GreetingCard.controller';
import { isBillingAddressValid } from '@apps/registry/guest/components/CheckoutDialog/hooks';
import { deriveAggregateCheckoutCodesResult } from '@apps/registry/common/utils/checkoutMechanisms';
import { composePurchaseReturnUrlTemplate } from '@apps/registry/guest/hooks/usePurchaseConfirmationLink';

export const DEFAULT_GIFT_CARD_PRICE = 3.99;
const FALLBACK_BASE_URL = 'https://withjoy.com';
export const THEMES: IGiftWrapTheme[] = [
  {
    id: 'love_bloom',
    gif: 'https://withjoy.com/media/raw/ecard-congrats/love-bloom.gif',
    thumbnail: 'https://withjoy.com/media/raw/ecard-congrats/love-bloom_thumb.jpg',
    background: 'rgb(248, 234, 226)',
    text: 'brandWarmGray6' as keyof Colors,
    accent: 'https://withjoy.com/media/raw/ecard-congrats/love-bloom-footer_SM_v2.gif',
    type: EGiftWrapType.BASIC,
    allowAsFirst: true,
  },
  {
    id: 'gettin_hitched',
    gif: 'https://withjoy.com/media/raw/ecard-congrats/gettin-hitched_700_v2.gif',
    thumbnail: 'https://withjoy.com/media/raw/ecard-congrats/gettin-hitched_thumb.jpg',
    background: 'rgb(255, 182, 183)',
    text: 'brandWarmGray6' as keyof Colors,
    accent: 'https://withjoy.com/media/raw/ecard-congrats/gettin-hitched_footer_SM.gif',
    type: EGiftWrapType.BASIC,
  },
  {
    id: 'eucalyptus_wreath',
    gif: 'https://withjoy.com/media/raw/ecard-congrats/eucalyptus-wreath.gif',
    thumbnail: 'https://withjoy.com/media/raw/ecard-congrats/eucalyptus-wreath_thumb.jpg',
    background: '#E7E5DF',
    text: 'brandWarmGray6' as keyof Colors,
    accent: 'https://withjoy.com/media/raw/ecard-congrats/eucalyptus_wreath_footer_SM.gif',
    type: EGiftWrapType.BASIC,
  },
  {
    id: 'love_sweet_love',
    gif: 'https://withjoy.com/media/raw/ecard-congrats/love-sweet-love_700_v2.gif',
    thumbnail: 'https://withjoy.com/media/raw/ecard-congrats/love-sweet-love_thumb.jpg',
    background: 'rgb(64, 147, 194)',
    text: 'white' as keyof Colors,
    accent: 'https://withjoy.com/media/raw/ecard-congrats/love-sweet-love_footer_SM.gif',
    type: EGiftWrapType.BASIC,
  },
  {
    id: 'happy_wedding_day',
    gif: 'https://withjoy.com/media/raw/ecard-congrats/happy-wedding-day_700_v2.gif',
    thumbnail: 'https://withjoy.com/media/raw/ecard-congrats/happy-wedding-day_thumb.jpg',
    background: 'rgb(21, 24, 38)',
    text: 'white' as keyof Colors,
    accent: 'https://withjoy.com/media/raw/ecard-congrats/happy-wedding-day_footer_SM.gif',
    type: EGiftWrapType.BASIC,
    allowAsFirst: true,
  },
];

const isValidEmail = (email: string) => !!email.match(/^([\w.%+-]+)@([\w-]+\.)+([\w]{2,})$/i);

const getInitialValues = (email = '', name = '', address?: ShippingAddressInput) => ({
  name,
  email,
  address1: address?.address1 ?? '140 Keller St',
  address2: address?.address2 ?? '#229',
  city: address?.city ?? 'Petaluma',
  state: address?.state ?? 'CA',
  postalCode: address?.postalCode ?? '94952',
  country: address?.country ?? 'United States',
  countryCode: address?.countryCode ?? 'USA',
  validated: address?.validated ?? true,
});

export const useOfferController = (eventHandle: string, message: string, setMessage: React.Dispatch<React.SetStateAction<string>>, queryData?: GetDataForThanksForRSVPQuery) => {
  const {
    giftWrapOfferViewed,
    giftWrapPromptSent,
    giftWrapMessageEdited,
    sendCongratulationsCardClicked,
    sendCongratulationsNoteClicked,
    trackError,
    giftWrapEditMessageClicked,
    uneditedWarningShown,
  } = useThanksForRSVPTelemetry();

  const uneditedMessageWarning = useDisclosure();

  const { t } = useTranslation('thanksForRSVP');
  const tCongratulationsCard = t('congratulationsCard');

  const [avoidAIGeneration, setAvoidAIGeneration] = useState<boolean>(!!message);
  const [tab, setTab] = useState<'wrap' | 'message'>('message');
  const [themeId, setThemeId] = useState<string>(THEMES[0].id);
  const [error, setError] = useState<string>('');
  const [isMessageEdited, setIsMessageEdited] = useState<boolean>(false);
  const [email, setEmail] = useState<string>('');

  const { getValueString } = useQueryParamHelper();
  const guestEmailFromURL = getValueString('guestEmail') ?? '';
  const guestEmail = isValidEmail(guestEmailFromURL) ? guestEmailFromURL : '';

  useEffect(() => {
    giftWrapOfferViewed({ eventHandle, type: GiftWrapType.RSVP, emailFieldDisplayed: !!!guestEmail });
  }, [eventHandle, giftWrapOfferViewed, guestEmail]);

  const guestNames = getValueString('guestNames');
  const isRegistryPublished = getValueString('isRegistryPublished') ?? 'true';
  const guestNamesArray = guestNames?.split(',') ?? [];
  const isPlural = guestNamesArray.length > 1;
  const guestName = isPlural ? guestNamesArray[0] : guestNames;
  const rsvp = getValueString('rsvp');

  const [saveGreetingCardAndEmailDraftMutation, { loading: saveGreetingCardAndEmailDraftMutationLoading, error: saveGreetingCardAndEmailDraftMutationError }] =
    useSaveGreetingCardAndEmailDraftMutation();
  const [createRegistryPurchaseContextMutation, { loading: createRegistryPurchaseContextMutationLoading, error: createRegistryPurchaseContextMutationError }] =
    useCreateRegistryPurchaseContextMutation();
  const [sendRsvpBasicNoteMutation, { loading: sendRsvpBasicNoteMutationLoading, error: sendRsvpBasicNoteMutationError }] = useSendRSVPBasicNoteMutation();

  const { payload: rsvpCongratulationsECardPriceFlagPayload } = useFeatureValue('rsvpCongratulationsECardPrice');
  const { payload: registryGiftWrapDefaultUSABillingAddressPayload } = useFeatureValue('registryGiftWrapDefaultUSABillingAddress');

  const rsvpCongratulationsECardPrice = isRegistryGiftWrapEcardPricePayload(rsvpCongratulationsECardPriceFlagPayload)
    ? rsvpCongratulationsECardPriceFlagPayload.price
    : DEFAULT_GIFT_CARD_PRICE;
  const price = `$${rsvpCongratulationsECardPrice}`;
  const defaultBillingAddress = isBillingAddressValid(registryGiftWrapDefaultUSABillingAddressPayload) ? registryGiftWrapDefaultUSABillingAddressPayload.address : undefined;
  const isRegistryEnabled = isRegistryPublished === 'true';
  const eventId = queryData?.eventByName?.id;
  const noEventId = !eventId;
  const fallbackUrl = new URL(withWindow(global => global.location.href, `${FALLBACK_BASE_URL}/${eventHandle}/${isRegistryEnabled ? 'registry' : ''}`)).toString();

  const firstPartner = queryData?.eventByName?.info?.ownerFirstName || '';
  const secondPartner = queryData?.eventByName?.info?.fianceeFirstName || '';
  const partners = `${firstPartner}${secondPartner ? ` and ${secondPartner}` : ''}` || tCongratulationsCard.theCouple();
  const prompt = `Simple first-person${isPlural ? ' plural' : ''} message starting with a salutation and congratulating ${partners} on their upcoming wedding in US english.${
    rsvp === 'all' ? ' Say that you are looking forward to attending the wedding.' : rsvp === 'none' ? ' Say that you are sorry to miss the wedding.' : ''
  } Message should finish with a signoff from ${guestNames}.`;
  const promptFallback = `Dear ${partners},

  Congratulations on your wedding!

  Wishing you a lifetime of love, happiness, and amazing adventures. Cheers!

  With love,
  ${guestNames}`;

  const { originalNote, isGeneratedMessageLoading } = useGenerateGiftNote({
    prompt,
    fallback: promptFallback,
    skip: avoidAIGeneration,
    onCompleted: (note, _) => {
      setMessage(note);
      setAvoidAIGeneration(true);
      giftWrapPromptSent({ prompt });
    },
  });

  const errorMessage =
    error || saveGreetingCardAndEmailDraftMutationError?.message || createRegistryPurchaseContextMutationError?.message || sendRsvpBasicNoteMutationError?.message;
  const isLoading = saveGreetingCardAndEmailDraftMutationLoading || createRegistryPurchaseContextMutationLoading || sendRsvpBasicNoteMutationLoading || isGeneratedMessageLoading;
  const isEmptyEmail = !guestEmail && !email;

  const handleSendCongratulationsCard = async () => {
    try {
      const formEmail = isValidEmail(email) ? email : '';
      const billingAddress = getInitialValues(guestEmail || formEmail, guestName, defaultBillingAddress);
      setError('');
      sendCongratulationsCardClicked({
        design: themeId,
        message,
        price,
        rsvpStatus: rsvp ?? '',
      });
      if (noEventId) {
        throw Error('No Event Id provided');
      }
      if (!billingAddress) {
        throw Error('No Billing Address provided');
      }
      if (!billingAddress.name) {
        throw Error('No Billing Name provided');
      }
      if (!billingAddress.email) {
        throw Error('No Valid Email provided');
      }
      const responseDraft = await saveGreetingCardAndEmailDraftMutation({
        variables: { payload: { type: 'rsvp', baseUrl: greetingCardBaseUrl, eventId, cardDetails: { message }, theme: themeId } },
      });
      const emailDraftId = responseDraft?.data?.saveGreetingCardAndEmailDraft?.id;
      if (!emailDraftId) {
        throw Error('No Email Draft Id provided');
      }
      const eCard = prepareECardPayload({
        price: rsvpCongratulationsECardPrice,
        draftId: emailDraftId,
        theme: themeId,
        offerType: GiftWrapType.RSVP,
      });
      const { email: billingAddressEmail, ...address } = billingAddress;
      const successUrlRoute = withWindow(global => global.location.href, fallbackUrl);
      const successUrlTemplate = composePurchaseReturnUrlTemplate(successUrlRoute) + '&success=true';
      const responseRegistry = await createRegistryPurchaseContextMutation({
        variables: {
          payload: {
            eventId,
            name: billingAddress.name,
            email: billingAddressEmail,
            address,
            successUrlTemplate,
            failureUrlTemplate: fallbackUrl,
            eCard,
          },
        },
      });
      // NOTE:  this has never been tested end-to-end
      const createRegistryPurchaseContext = responseRegistry?.data?.createRegistryPurchaseContext ?? null;
      const { checkoutFailureCode } = deriveAggregateCheckoutCodesResult([createRegistryPurchaseContext]);
      const checkoutUrl = createRegistryPurchaseContext?.checkoutUrl;

      if (!checkoutUrl || checkoutFailureCode) {
        // TODO:  something explanatory like PaymentErrorScreen
        throw Error('No Stripe URL provided');
      }
      withWindow(global => (global.location.href = checkoutUrl));
    } catch (error) {
      trackError(error);
      setError((error as Error)?.message || 'Generic Error');
    }
  };

  const handleSendCongratulationsNote = async () => {
    try {
      const emailForReplies = guestEmail ? guestEmail : isValidEmail(email) ? email : '';
      setError('');
      sendCongratulationsNoteClicked({
        message,
        rsvpStatus: rsvp ?? '',
      });
      if (noEventId) {
        throw Error('No Event Id provided');
      }
      if (!emailForReplies) {
        throw Error('No Valid Email provided');
      }
      if (!guestName) {
        throw Error('No Valid Giver Name provided');
      }
      const responseDraft = await saveGreetingCardAndEmailDraftMutation({
        variables: { payload: { type: 'rsvp-basic', baseUrl: greetingCardBaseUrl, eventId, cardDetails: { message }, theme: 'basic' } },
      });
      const emailDraftId = responseDraft?.data?.saveGreetingCardAndEmailDraft?.id;
      if (!emailDraftId) {
        throw Error('No Email Draft Id provided');
      }
      await sendRsvpBasicNoteMutation({ variables: { payload: { draftId: emailDraftId, eventId, giverName: guestName, replyToEmail: emailForReplies } } });
      const successUrl = withWindow(global => new URL(`${global.location.href}&success=true`).toString(), fallbackUrl);
      withWindow(global => (global.location.href = successUrl));
    } catch (error) {
      trackError(error);
      setError((error as Error)?.message || 'Generic Error');
    }
  };

  const handleSend = (skipEditedValidation = false) => {
    if (!skipEditedValidation && !isMessageEdited) {
      uneditedMessageWarning.onOpen();
      uneditedWarningShown();
      return;
    }

    return tab === 'message' ? handleSendCongratulationsNote() : handleSendCongratulationsCard();
  };

  const handleOnChangeTextArea = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (!isMessageEdited) {
      setIsMessageEdited(true);
    }
    setMessage(e.target.value);
  };

  const handleOnBlurTextArea = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (!isMessageEdited) {
      setIsMessageEdited(true);
    }
    giftWrapMessageEdited({ originalNote, modifiedNote: e.target.value });
  };

  const handleBlur = ({ originalNote, message }: { originalNote: string; message: string }) => {
    if (!isMessageEdited) {
      setIsMessageEdited(true);
    }
    giftWrapMessageEdited({ originalNote, modifiedNote: message });
  };

  const handleEditMessageClick = (label: string) => {
    giftWrapEditMessageClicked({ label });
  };

  const handleWrapClick = () => {
    if (tab === 'wrap') {
      return;
    }
    setTab('wrap');
  };

  const handleMessageClick = () => {
    if (tab === 'message') {
      return;
    }
    setTab('message');
  };

  const handleContinueClick = () => {
    uneditedMessageWarning.onClose();
    handleSend(true);
  };

  return {
    setThemeId,
    price,
    isLoading,
    tCongratulationsCard,
    THEMES,
    prompt,
    promptFallback,
    handleBlur,
    handleEditMessageClick,
    tab,
    uneditedMessageWarning,
    isGeneratedMessageLoading,
    noEventId,
    guestEmail,
    email,
    setEmail,
    errorMessage,
    handleSend,
    isEmptyEmail,
    handleWrapClick,
    handleMessageClick,
    handleContinueClick,
    handleOnChangeTextArea,
    handleOnBlurTextArea,
  };
};
